/**
 * SOLID Review
 * S — Strong. One concern: which tab of the legal modal shows.
 * O — Good. A new tab is one more member of `LegalTab` (and its tab in the view).
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. One action.
 * D — Strong. Imports only the view-model contract and its state.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';

/** The legal modal's tabs. GDPR "Your Rights" (export / delete account) is the Profile modal's (account actions, login-gated). */
export type LegalTab = 'privacy' | 'terms' | 'cookies' | 'security';

export interface IStateLegalModal {
  readonly activeTab: LegalTab;
}

/**
 * THE LEGAL MODAL'S TAB (frontend-framework-oo P3, 2026-10-29, §28.295) — `legal/components/LegalModal.tsx`'s state: seeded with the
 * tab the modal first mounts with and kept from then on — a later `initialTab` does not move it, as `useState` seeded once (the modal
 * stays mounted while closed, so the tab it opens on is the one it last showed).
 */
export class LegalModalViewModel implements IServiceViewModel<IStateLegalModal> {
  private readonly state: ObservableState<IStateLegalModal>;

  constructor(initialTab: LegalTab) {
    const seeded: IStateLegalModal = { activeTab: initialTab };
    this.state = new ObservableState<IStateLegalModal>(seeded);
  }

  readonly select = (activeTab: LegalTab): void => {
    const selected: Partial<IStateLegalModal> = { activeTab };
    this.state.update(selected);
  };

  getSnapshot(): IStateLegalModal {
    return this.state.get();
  }

  subscribe(listener: () => void): () => void {
    return this.state.subscribe(listener);
  }

  attach(): void {}

  detach(): void {}
}
